import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '../..';
import { type LeaseSettingsPayload } from '../../../../../../api';
import { type NumberFormatFn } from '../../../../../../utilities/useFormats';
import { getFinderVehicleFilter, getVariantFilter } from '../../../shared/useVehicleSuiteIdsFromParameters';
import getFinderVariantNameWithParameters from '../../helpers/getFinderVariantNameWithParameters';
import hasVehicleParameterFilter from '../../helpers/hasVehicleParameterFilter';
import {
    buildFinderVehicleReferenceParameterName,
    finderVehicleParameterKey,
    variantParameterKey,
} from '../LeasePurchaseTable/useVehicleReferenceParameterRecords';
import { type LeaseTableRecord } from './shared';

type Params = {
    vehicleReferenceParameters: FinanceProductFormValues['vehicleReferenceParameters'];
    leaseSettings: LeaseSettingsPayload;
    formatAmount: NumberFormatFn;
    terms: number[];
};

type ReturnType = {
    variant: LeaseTableRecord[];
    finderVehicle: LeaseTableRecord[];
};

const useVehicleReferenceParameterRecords = ({
    vehicleReferenceParameters,
    leaseSettings,
    formatAmount,
    terms,
}: Params): ReturnType => {
    const { t } = useTranslation(['financeProductDetails']);
    const mileageUnitLabel = t('financeProductDetails:suffix.mileage');

    const vehicleReferenceParameterData: ReturnType = useMemo(() => {
        const data: ReturnType = {
            finderVehicle: [],
            variant: [],
        };

        const finderVehicleFilter = getFinderVehicleFilter(vehicleReferenceParameters);
        const variantFilter = getVariantFilter(vehicleReferenceParameters);

        if (
            !leaseSettings ||
            !terms.length ||
            (!hasVehicleParameterFilter(finderVehicleFilter) && !hasVehicleParameterFilter(variantFilter))
        ) {
            return data;
        }

        const variantReferenceParameterId = vehicleReferenceParameters.variant.engineType.length
            ? vehicleReferenceParameters.variant.engineType.join(', ')
            : null;
        const finderVehicleReferenceParameterId = buildFinderVehicleReferenceParameterName(
            vehicleReferenceParameters.finderVehicle,
            mileageUnitLabel
        );

        const { table } = leaseSettings;

        const getValue = (variantSuiteId: string, term: number) => {
            const row = table.find(row => row.variantSuiteId === variantSuiteId && row.term === term);

            return row?.value;
        };

        if (hasVehicleParameterFilter(variantFilter) && variantFilter.engineType.length) {
            const termsValue = {};

            terms.forEach(term => {
                termsValue[`term${term}`] = getValue(variantParameterKey, term);
            });

            data.variant = [
                {
                    key: variantParameterKey,
                    variantSuiteId: variantParameterKey,
                    variantId: variantParameterKey,
                    variantName: variantReferenceParameterId,
                    variantNameFromParameter: variantReferenceParameterId,
                    ...termsValue,
                },
            ];
        }

        if (hasVehicleParameterFilter(finderVehicleFilter)) {
            const termsValue = {};

            terms.forEach(term => {
                termsValue[`term${term}`] = getValue(finderVehicleParameterKey, term);
            });

            data.finderVehicle = [
                {
                    key: finderVehicleParameterKey,
                    variantSuiteId: finderVehicleParameterKey,
                    variantId: finderVehicleParameterKey,
                    variantName: finderVehicleReferenceParameterId,
                    variantNameFromParameter: getFinderVariantNameWithParameters(
                        vehicleReferenceParameters,
                        mileageUnitLabel,
                        formatAmount
                    ),
                    ...termsValue,
                },
            ];
        }

        return data;
    }, [vehicleReferenceParameters, leaseSettings, terms, mileageUnitLabel, formatAmount]);

    return vehicleReferenceParameterData;
};

export default useVehicleReferenceParameterRecords;
